import React, { FC, memo, useState } from "react";
import { Avatar, Dropdown, MenuProps, Modal, Badge } from "antd";
import { User, KeyRound, LogOut, ChevronDown, Bell, ExternalLink } from "lucide-react";
import { useUserStore } from "../../store/slices/user";
import { useUserLogout } from "../../hooks/api/user/hooks";
import ProfileModal from "../components/ProfileModal";
import PasswordModal from "../components/PasswordModal";
import { useGetUnreadNotificationCount } from "../../hooks/api/notification/hooks";
import { useNavigate } from "react-router-dom";

// Not config.siteUrl - that var is actually wired to the admin panel's own
// URL (REACT_APP_PROD_SITE_URL=https://admin.artparks.co.uk/ in admin/.env,
// unused anywhere else), not the public website. Hardcoded here instead of
// threading a new env var through the Docker build for one link.
const PUBLIC_SITE_URL = "https://artparks.co.uk";

interface TopProps {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  showDrawer: () => void;
}

const Top: FC<TopProps> = () => {
  const navigate = useNavigate();
  const { user, logout } = useUserStore();
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  // Notification Count Hook
  const { data: unreadCountData } = useGetUnreadNotificationCount();
  const unreadCount = unreadCountData?.data?.unreadCount || 0;

  const logoutMutation = useUserLogout({
    onSuccess: () => {
      logout();
      setLogoutModalOpen(false);
    },
    onError: () => {
      logout();
      setLogoutModalOpen(false);
    },
  });

  const handleConfirmLogout = () => {
    logoutMutation.mutate();
  };

  const dropdownItems: MenuProps["items"] = [
    {
      key: "user-info",
      label: (
        <div className="px-1 py-1 border-b border-slate-100 mb-1">
          <p className="font-semibold text-slate-800 text-sm leading-tight">
            {user?.name || "Admin User"}
          </p>
          <p className="text-xs text-slate-500 truncate mt-0.5">{user?.email || ""}</p>
        </div>
      ),
      disabled: true,
    },
    {
      key: "profile",
      icon: <User size={16} className="text-slate-600" />,
      label: "Update Profile",
      onClick: () => setProfileModalOpen(true),
    },
    {
      key: "password",
      icon: <KeyRound size={16} className="text-slate-600" />,
      label: "Change Password",
      onClick: () => setPasswordModalOpen(true),
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      icon: <LogOut size={16} className="text-red-500" />,
      label: <span className="text-red-600 font-medium">Logout</span>,
      onClick: () => setLogoutModalOpen(true),
    },
  ];

  return (
    <>
      {/* TOPBAR */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between">
        {/* LEFT */}
        <div className="flex items-center gap-3">
          {/* Global search removed as requested */}
        </div>

        {/* RIGHT ACTIONS */}
        <div className="flex items-center gap-4">
          {/* VIEW SITE - Oliver, Sept 2026 */}
          <a
            href={PUBLIC_SITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-primary transition-colors px-2 py-1.5 rounded-lg hover:bg-slate-100"
          >
            <ExternalLink size={16} />
            View Site
          </a>

          {/* NOTIFICATIONS BELL ICON */}
          <div
            onClick={() => navigate("/notifications")}
            className="relative cursor-pointer p-2 rounded-lg hover:bg-slate-100 transition-colors"
            title="View Notifications"
          >
            <Badge count={unreadCount} overflowCount={99} size="small">
              <Bell size={20} className="text-slate-600 hover:text-primary transition-colors" />
            </Badge>
          </div>

          {/* USER PROFILE DROPDOWN */}
          <Dropdown menu={{ items: dropdownItems }} trigger={["click"]} placement="bottomRight">
            <div className="flex items-center gap-2 cursor-pointer p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
              <Avatar className="bg-primary text-white font-semibold">
                {user?.name ? user.name.charAt(0).toUpperCase() : "A"}
              </Avatar>
              <div className="hidden sm:block text-left">
                <div className="text-sm font-medium text-slate-800 leading-none">
                  {user?.name || "Admin"}
                </div>
                <div className="text-xs text-slate-500 leading-none mt-1">
                  {(user as any)?.user_type || "Super Admin"}
                </div>
              </div>
              <ChevronDown size={16} className="text-slate-400" />
            </div>
          </Dropdown>
        </div>
      </div>

      {/* MODALS */}
      <ProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />

      <PasswordModal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />

      <Modal
        title="Logout Confirmation"
        open={logoutModalOpen}
        onOk={handleConfirmLogout}
        onCancel={() => setLogoutModalOpen(false)}
        confirmLoading={logoutMutation.isPending}
        okText="Yes, Logout"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to logout from the Admin Portal?</p>
      </Modal>
    </>
  );
};

export default memo(Top);
